import { useState } from 'react';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  FilterProvider,
  FilterField,
  assignmentFilterConfig,
  type AssignmentFilters,
} from '@/components/filters';

interface PickerFilterSheetProps {
  filters: AssignmentFilters;
  onChange: (filters: AssignmentFilters) => void;
  activeCount: number;
  onClear: () => void;
}

export function PickerFilterSheet({
  filters,
  onChange,
  activeCount,
  onClear,
}: PickerFilterSheetProps) {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-9"
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs ml-1">
              {activeCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[320px] sm:w-[380px]">
        <SheetHeader className="pb-4">
          <div className="flex items-center justify-between">
            <SheetTitle>Filter Employees</SheetTitle>
            {activeCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClear}
                className="h-8 text-xs text-muted-foreground"
              >
                Clear all
              </Button>
            )}
          </div>
        </SheetHeader>

        <FilterProvider config={assignmentFilterConfig} value={filters} onChange={onChange}>
          <div className="space-y-5">
            {assignmentFilterConfig.fields.map((fieldConfig) => (
              <FilterField
                key={fieldConfig.key}
                config={fieldConfig}
                value={filters[fieldConfig.key as keyof AssignmentFilters]}
                onChange={(value) => {
                  onChange({
                    ...filters,
                    [fieldConfig.key]: value,
                  });
                }}
              />
            ))}
          </div>
        </FilterProvider>

        <div className="mt-6 pt-4 border-t">
          <Button onClick={() => setOpen(false)} className="w-full">
            Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
